import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Table, Button, Form, Spinner } from "react-bootstrap";
import { FaPlus, FaTrash, FaCheck, FaTimes, FaEdit, FaRupeeSign } from "react-icons/fa";
import { getAuthToken } from "../../../util/auth";
import { getApiChara } from "../../../util/getApi";
import { toast } from "react-toastify";
import ConfirmModal from "../../UI/ConfirmModal";

const ChargeMasterManagement = () => {
    const { t } = useTranslation();
    const [charges, setCharges] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editRowData, setEditRowData] = useState({});

    const [formData, setFormData] = useState({
        chargeName: "",
        chargeCode: "",
        category: "",
        amount: "",
    });

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [idToDelete, setIdToDelete] = useState(null);

    const fetchCharges = async () => {
        const token = getAuthToken();
        const apiChar = getApiChara();
        setLoading(true);
        try {
            const response = await fetch(`${apiChar}/charge-master`, {
                headers: { Authorization: "Bearer " + token },
            });
            if (response.ok) {
                const data = await response.json();
                setCharges(data || []);
            }
        } catch (error) {
            console.error("Error fetching charges:", error);
            toast.error(t("Error fetching charge master data"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCharges();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        if (!formData.chargeName.trim() || !formData.chargeCode.trim() || !formData.amount) {
            toast.error(t("Please fill mandatory fields (Name, Code and Amount)"));
            return;
        }

        // Check for unique code
        const isDuplicate = charges.some(c => c.chargeCode?.trim().toLowerCase() === formData.chargeCode.trim().toLowerCase());
        if (isDuplicate) {
            toast.error(t("Charge Code must be unique. This code already exists."));
            return;
        }

        setIsSaving(true);
        const token = getAuthToken();
        const apiChar = getApiChara();
        const url = `${apiChar}/charge-master`;

        const payload = {
            chargeName: formData.chargeName.trim(),
            chargeCode: formData.chargeCode.trim(),
            category: formData.category.trim(),
            amount: parseFloat(formData.amount) || 0,
            option1: ""
        };

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token,
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                toast.success(t("Charge added successfully!"));
                setFormData({ chargeName: "", chargeCode: "", category: "", amount: "" });
                fetchCharges();
            } else {
                throw new Error("Failed to add charge");
            }
        } catch (error) {
            console.error(error);
            toast.error(t("Error adding charge"));
        } finally {
            setIsSaving(false);
        }
    };

    const handleEdit = (charge) => {
        setEditingId(charge.id);
        setEditRowData({ ...charge });
    };

    const handleInlineUpdate = async () => {
        if (!editRowData.chargeName?.trim() || !editRowData.chargeCode?.trim() || isNaN(parseFloat(editRowData.amount))) {
            toast.error(t("Name, Code and Amount are required and must be valid"));
            return;
        }

        // Check for unique code (excluding self)
        const isDuplicate = charges.some(c => 
            c.id !== editingId && 
            c.chargeCode?.trim().toLowerCase() === editRowData.chargeCode.trim().toLowerCase()
        );
        if (isDuplicate) {
            toast.error(t("Charge Code must be unique. This code already exists in another record."));
            return;
        }

        setIsSaving(true);
        const token = getAuthToken();
        const apiChar = getApiChara();
        const url = `${apiChar}/charge-master/${editingId}`;

        const payload = {
            id: editingId,
            chargeName: editRowData.chargeName.trim(),
            chargeCode: editRowData.chargeCode?.trim() || "",
            category: editRowData.category?.trim() || "",
            amount: parseFloat(editRowData.amount) || 0,
            option1: ""
        };

        try {
            const response = await fetch(url, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token,
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                toast.success(t("Charge updated successfully!"));
                setEditingId(null);
                setEditRowData({});
                fetchCharges();
            } else {
                throw new Error("Failed to update charge");
            }
        } catch (error) {
            console.error(error);
            toast.error(t("Error updating charge"));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = (id) => {
        setIdToDelete(id);
        setShowDeleteModal(true);
    };

    const confirmDeleteHandler = async () => {
        if (!idToDelete) return;

        const token = getAuthToken();
        const apiChar = getApiChara();
        setLoading(true);
        try {
            const response = await fetch(`${apiChar}/charge-master/${idToDelete}`, {
                method: "DELETE",
                headers: { Authorization: "Bearer " + token },
            });
            if (response.ok) {
                toast.success(t("Charge deleted successfully!"));
                fetchCharges();
            }
        } catch (error) {
            console.error(error);
            toast.error(t("Error deleting charge"));
        } finally {
            setLoading(false);
            setShowDeleteModal(false);
            setIdToDelete(null);
        }
    };

    return (
        <div className="charge-master-management">
            <Form onSubmit={handleAddSubmit} className="mb-4 p-3 border rounded bg-light shadow-sm">
                <h5 className="mb-3" style={{ color: "var(--primary-color)", fontWeight: "700" }}>
                    {t("Add New Charge Record")}
                </h5>
                <div className="row g-3">
                    <div className="col-md-4 col-12">
                        <Form.Label style={{ fontWeight: "600", fontSize: "0.85rem" }}>{t("Name")}</Form.Label>
                        <Form.Control
                            type="text"
                            name="chargeName"
                            value={formData.chargeName}
                            onChange={handleInputChange}
                            placeholder={t("e.g., Consultation Fee")}
                            style={{ fontSize: "0.9rem", border: "1px solid #ced4da" }}
                            required
                        />
                    </div>
                    <div className="col-md-2 col-12">
                        <Form.Label style={{ fontWeight: "600", fontSize: "0.85rem" }}>
                            {t("Code")} <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            name="chargeCode"
                            value={formData.chargeCode}
                            onChange={handleInputChange}
                            placeholder={t("e.g., C001")}
                            style={{ fontSize: "0.9rem", border: "1px solid #ced4da" }}
                        />
                    </div>
                    <div className="col-md-2 col-12">
                        <Form.Label style={{ fontWeight: "600", fontSize: "0.85rem" }}>{t("Category")}</Form.Label>
                        <Form.Control
                            type="text"
                            name="category"
                            value={formData.category}
                            onChange={handleInputChange}
                            placeholder={t("e.g., OPD")}
                            style={{ fontSize: "0.9rem", border: "1px solid #ced4da" }}
                        />
                    </div>
                    <div className="col-md-2 col-12">
                        <Form.Label style={{ fontWeight: "600", fontSize: "0.85rem" }}>{t("Amount")}</Form.Label>
                        <Form.Control
                            type="number"
                            name="amount"
                            value={formData.amount}
                            onChange={handleInputChange}
                            placeholder={t("0.00")}
                            style={{ fontSize: "0.9rem", border: "1px solid #ced4da" }}
                            required
                        />
                    </div>
                    <div className="col-md-2 col-12 d-flex align-items-end">
                        <Button type="submit" className="btn-premium btn-sm w-100 py-2 d-flex align-items-center justify-content-center" disabled={isSaving}>
                            <FaPlus className="me-2" /> {t("Add Record")}
                        </Button>
                    </div>
                </div>
            </Form>

            <div className="records-list mt-4">
                <h5 className="mb-3" style={{ color: "var(--primary-color)", fontWeight: "700" }}>{t("Saved Details")}</h5>

                <div className="table-responsive border rounded shadow-sm">
                    <Table striped hover className="mb-0 align-middle" style={{ fontSize: "0.85rem" }}>
                        <thead style={{ backgroundColor: "#f8f9fa" }}>
                            <tr className="bg-light">
                                <th style={{ width: "60px", textAlign: "center" }} className="py-3">{t("Sr.No")}</th>
                                <th className="py-3">{t("Name")}</th>
                                <th style={{ width: "120px" }} className="py-3">{t("Code")}</th>
                                <th style={{ width: "120px" }} className="py-3">{t("Category")}</th>
                                <th className="text-end px-4 py-3" style={{ width: "140px" }}>{t("Amount")}</th>
                                <th style={{ width: "140px" }} className="text-center py-3">{t("Actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && charges.length === 0 ? (
                                <tr><td colSpan="6" className="text-center py-4"><Spinner animation="border" size="sm" /></td></tr>
                            ) : charges.length === 0 ? (
                                <tr><td colSpan="6" className="text-center py-4 text-muted">{t("No charge records found")}</td></tr>
                            ) : (
                                charges.map((charge, index) => (
                                    <tr key={charge.id || index}>
                                        <td className="text-center">{index + 1}</td>
                                        <td>
                                            {editingId === charge.id ? (
                                                <Form.Control
                                                    type="text"
                                                    value={editRowData.chargeName}
                                                    onChange={(e) => setEditRowData({ ...editRowData, chargeName: e.target.value })}
                                                    size="sm"
                                                    autoFocus
                                                />
                                            ) : (
                                                <span className="fw-500">{charge.chargeName}</span>
                                            )}
                                        </td>
                                        <td>
                                            {editingId === charge.id ? (
                                                <Form.Control
                                                    type="text"
                                                    value={editRowData.chargeCode}
                                                    onChange={(e) => setEditRowData({ ...editRowData, chargeCode: e.target.value })}
                                                    size="sm"
                                                />
                                            ) : (
                                                charge.chargeCode || "-"
                                            )}
                                        </td>
                                        <td>
                                            {editingId === charge.id ? (
                                                <Form.Control
                                                    type="text"
                                                    value={editRowData.category}
                                                    onChange={(e) => setEditRowData({ ...editRowData, category: e.target.value })}
                                                    size="sm"
                                                />
                                            ) : (
                                                charge.category || "-"
                                            )}
                                        </td>
                                        <td className="text-end px-4 font-monospace">
                                            {editingId === charge.id ? (
                                                <Form.Control
                                                    type="number"
                                                    value={editRowData.amount}
                                                    onChange={(e) => setEditRowData({ ...editRowData, amount: e.target.value })}
                                                    size="sm"
                                                    className="text-end"
                                                />
                                            ) : (
                                                <>
                                                    <FaRupeeSign className="small me-1 text-muted" />
                                                    {parseFloat(charge.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                </>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <div className="d-flex justify-content-center gap-2">
                                                {editingId === charge.id ? (
                                                    <>
                                                        <Button
                                                            variant="success"
                                                            size="sm"
                                                            className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                                                            onClick={handleInlineUpdate}
                                                            title={t("Save")}
                                                            disabled={isSaving}
                                                            style={{ width: '32px', height: '32px' }}
                                                        >
                                                            {isSaving ? <Spinner animation="border" size="sm" /> : <FaCheck size={14} />}
                                                        </Button>
                                                        <Button
                                                            variant="secondary"
                                                            size="sm"
                                                            className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                                                            onClick={() => setEditingId(null)}
                                                            title={t("Cancel")}
                                                            style={{ width: '32px', height: '32px' }}
                                                        >
                                                            <FaTimes size={14} />
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Button
                                                            variant="link"
                                                            className="text-primary p-1"
                                                            onClick={() => handleEdit(charge)}
                                                            title={t("Edit")}
                                                            disabled={editingId !== null}
                                                        >
                                                            <FaEdit size={16} />
                                                        </Button>
                                                        <Button
                                                            variant="link"
                                                            className="text-danger p-1"
                                                            onClick={() => handleDelete(charge.id)}
                                                            title={t("Delete")}
                                                            disabled={editingId !== null}
                                                        >
                                                            <FaTrash size={16} />
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>

            {/* Confirmation for Delete */}
            <ConfirmModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={confirmDeleteHandler}
                title={t("Delete Charge Record")}
                message={t("Are you sure you want to delete this charge record?")}
                variant="danger"
                isLoading={loading}
                confirmText={t("Delete")}
            />
        </div>
    );
};

export default ChargeMasterManagement;
